import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import ApiService from '../../services/api.service';
import { PLAN_LABELS_ES } from '../../utils/planTiers';
import { formatLocaleEs } from '../../utils/safeDate';

const api = new ApiService('/admin');

const METHOD_LABEL = {
  manual_transfer: 'Transferencia',
  manual_cash: 'Efectivo',
  manual_other: 'Otro'
};

const REJECT_REASONS = [
  { id: 'pago_no_encontrado', label: 'Pago no encontrado' },
  { id: 'importe_incorrecto', label: 'Importe incorrecto' },
  { id: 'referencia_invalida', label: 'Referencia inválida' },
  { id: 'comprobante_insuficiente', label: 'Comprobante insuficiente' },
  { id: 'datos_incompletos', label: 'Datos incompletos' },
  { id: 'otro', label: 'Otro' }
];

function formatArs(n) {
  try {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      maximumFractionDigits: 0
    }).format(Number(n) || 0);
  } catch {
    return `ARS ${n}`;
  }
}

/**
 * Panel admin — solicitudes de pago manual (001C-D).
 */
const ManualPaymentAdminSection = () => {
  const [statusFilter, setStatusFilter] = useState('pending_review');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const limit = 25;
  const [detail, setDetail] = useState(null);
  const [approveOpen, setApproveOpen] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(null);
  const [infoOpen, setInfoOpen] = useState(null);
  const [approveRef, setApproveRef] = useState('');
  const [approveConfirm, setApproveConfirm] = useState(false);
  const [approveObs, setApproveObs] = useState('');
  const [approveBusy, setApproveBusy] = useState(false);
  const [rejectReason, setRejectReason] = useState('pago_no_encontrado');
  const [rejectObs, setRejectObs] = useState('');
  const [rejectBusy, setRejectBusy] = useState(false);
  const [infoMsg, setInfoMsg] = useState('');
  const [infoBusy, setInfoBusy] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        status: statusFilter || '',
        limit: String(limit),
        offset: String(offset)
      });
      const { data, status } = await api.get(`/manual-payment-requests?${params.toString()}`);
      if (status === 200 && data?.success) {
        setRows(data.data?.requests || []);
        setTotal(data.data?.total || 0);
        setPendingCount(data.data?.pendingCount || 0);
      } else if (status === 403) {
        toast.error('Solo administradores');
      } else {
        toast.error(data?.message || 'No se pudieron cargar solicitudes');
      }
    } catch {
      toast.error('Error al cargar solicitudes manuales');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, offset]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const abrirDetalle = async (row) => {
    try {
      const { data, status } = await api.get(
        `/manual-payment-requests/${encodeURIComponent(row.orgId)}/${encodeURIComponent(row.requestId)}`
      );
      if (status === 200 && data?.success) setDetail(data.data);
      else toast.error(data?.message || 'No se pudo cargar el detalle');
    } catch {
      toast.error('Error al cargar detalle');
    }
  };

  const abrirAprobar = async (row) => {
    await abrirDetalle(row);
    setApproveRef(row.paymentReference || '');
    setApproveObs('');
    setApproveConfirm(false);
    setApproveOpen(row);
  };

  const confirmarAprobar = async () => {
    if (!approveOpen) return;
    if (!approveConfirm) {
      toast.warning('Confirmá que verificaste el pago');
      return;
    }
    if (!String(approveRef || '').trim()) {
      toast.error('Referencia de pago obligatoria');
      return;
    }
    setApproveBusy(true);
    try {
      const { data, status } = await api.post(
        `/manual-payment-requests/${encodeURIComponent(approveOpen.orgId)}/${encodeURIComponent(approveOpen.requestId)}/approve`,
        {
          paymentReference: approveRef.trim(),
          confirmedAmount: approveOpen.expectedAmount,
          currency: 'ARS',
          adminObservation: approveObs || undefined
        }
      );
      if (status >= 200 && status < 300 && data?.success) {
        const reused = data.data?.alreadyApplied;
        toast.success(
          reused
            ? 'Ya estaba aplicada (idempotente). Sin segunda extensión.'
            : 'Pago aprobado y plan activado.'
        );
        setApproveOpen(null);
        setDetail(null);
        await cargar();
      } else {
        toast.error(data?.message || data?.code || 'No se pudo aprobar');
      }
    } catch (e) {
      toast.error(e?.message || 'Error al aprobar');
    } finally {
      setApproveBusy(false);
    }
  };

  const confirmarRechazar = async () => {
    if (!rejectOpen) return;
    if (rejectReason === 'otro' && !String(rejectObs || '').trim()) {
      toast.error('Observación obligatoria para «Otro»');
      return;
    }
    setRejectBusy(true);
    try {
      const { data, status } = await api.post(
        `/manual-payment-requests/${encodeURIComponent(rejectOpen.orgId)}/${encodeURIComponent(rejectOpen.requestId)}/reject`,
        {
          rejectionReason: rejectReason,
          adminObservation: rejectObs || undefined
        }
      );
      if (status >= 200 && status < 300 && data?.success) {
        toast.info('Solicitud rechazada');
        setRejectOpen(null);
        await cargar();
      } else {
        toast.error(data?.message || 'No se pudo rechazar');
      }
    } catch {
      toast.error('Error al rechazar');
    } finally {
      setRejectBusy(false);
    }
  };

  const confirmarInfo = async () => {
    if (!infoOpen) return;
    if (String(infoMsg || '').trim().length < 5) {
      toast.error('Mensaje demasiado corto');
      return;
    }
    setInfoBusy(true);
    try {
      const { data, status } = await api.post(
        `/manual-payment-requests/${encodeURIComponent(infoOpen.orgId)}/${encodeURIComponent(infoOpen.requestId)}/request-info`,
        { message: infoMsg.trim() }
      );
      if (status >= 200 && status < 300 && data?.success) {
        toast.info('Pedimos información al cliente');
        setInfoOpen(null);
        setInfoMsg('');
        await cargar();
      } else {
        toast.error(data?.message || 'No se pudo solicitar información');
      }
    } catch {
      toast.error('Error al solicitar información');
    } finally {
      setInfoBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-4 border border-gray-100" data-testid="manual-payment-admin">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Solicitudes de pago manual</h2>
          <p className="text-sm text-gray-600">
            Pendientes:{' '}
            <strong className="text-amber-700" data-testid="manual-pending-count">
              {pendingCount}
            </strong>
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <label className="text-xs text-gray-500">
            Estado
            <select
              className="ml-2 border rounded px-2 py-1 text-sm"
              value={statusFilter}
              onChange={(e) => {
                setOffset(0);
                setStatusFilter(e.target.value);
              }}
              data-testid="manual-status-filter"
            >
              <option value="pending_review">En revisión</option>
              <option value="approved">Aprobadas</option>
              <option value="rejected">Rechazadas</option>
              <option value="cancelled">Canceladas</option>
              <option value="superseded">Reemplazadas (MP)</option>
              <option value="">Todas</option>
            </select>
          </label>
          <button type="button" className="px-3 py-1.5 rounded bg-gray-200 text-sm" onClick={cargar}>
            Actualizar
          </button>
        </div>
      </div>

      {loading ? (
        <p className="mt-4 text-sm text-gray-500">Cargando…</p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b">
                <th className="py-2 pr-3">Empresa</th>
                <th className="py-2 pr-3">Plan</th>
                <th className="py-2 pr-3">Importe</th>
                <th className="py-2 pr-3">Medio</th>
                <th className="py-2 pr-3">Fecha</th>
                <th className="py-2 pr-3">Estado</th>
                <th className="py-2 pr-3">Referencia</th>
                <th className="py-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.orgId}_${r.requestId}`} className="border-b border-gray-50">
                  <td className="py-2 pr-3 font-medium text-gray-800">{r.empresa || r.orgId}</td>
                  <td className="py-2 pr-3">{r.planName || PLAN_LABELS_ES[r.planId] || r.planId}</td>
                  <td className="py-2 pr-3">{formatArs(r.expectedAmount)}</td>
                  <td className="py-2 pr-3">{METHOD_LABEL[r.paymentMethod] || r.paymentMethod}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">
                    {r.submittedAt ? formatLocaleEs(r.submittedAt) : '—'}
                  </td>
                  <td className="py-2 pr-3">
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">{r.status}</span>
                    {r.needsMoreInfo ? (
                      <span className="ml-1 text-xs text-amber-700">info</span>
                    ) : null}
                  </td>
                  <td className="py-2 pr-3 max-w-[8rem] truncate">{r.paymentReference || '—'}</td>
                  <td className="py-2 space-x-1 whitespace-nowrap">
                    <button
                      type="button"
                      className="text-indigo-700 text-xs font-semibold"
                      onClick={() => abrirDetalle(r)}
                    >
                      Ver
                    </button>
                    {r.status === 'pending_review' ? (
                      <>
                        <button
                          type="button"
                          className="text-emerald-700 text-xs font-semibold"
                          onClick={() => abrirAprobar(r)}
                          data-testid="manual-admin-approve-open"
                        >
                          Aprobar
                        </button>
                        <button
                          type="button"
                          className="text-rose-700 text-xs font-semibold"
                          onClick={() => {
                            setRejectReason('pago_no_encontrado');
                            setRejectObs('');
                            setRejectOpen(r);
                          }}
                        >
                          Rechazar
                        </button>
                        <button
                          type="button"
                          className="text-amber-700 text-xs font-semibold"
                          onClick={() => {
                            setInfoMsg('');
                            setInfoOpen(r);
                          }}
                        >
                          Pedir info
                        </button>
                      </>
                    ) : null}
                  </td>
                </tr>
              ))}
              {!rows.length ? (
                <tr>
                  <td colSpan={8} className="py-6 text-center text-gray-500">
                    Sin solicitudes en este filtro
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
            <span>
              {offset + 1}–{Math.min(offset + limit, total)} de {total}
            </span>
            <div className="space-x-2">
              <button
                type="button"
                disabled={offset <= 0}
                className="px-2 py-1 border rounded disabled:opacity-40"
                onClick={() => setOffset((o) => Math.max(0, o - limit))}
              >
                Anterior
              </button>
              <button
                type="button"
                disabled={offset + limit >= total}
                className="px-2 py-1 border rounded disabled:opacity-40"
                onClick={() => setOffset((o) => o + limit)}
              >
                Siguiente
              </button>
            </div>
          </div>
        </div>
      )}

      {detail ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-5 shadow-xl">
            <h3 className="text-lg font-bold text-gray-900">Detalle solicitud</h3>
            <dl className="mt-3 space-y-1 text-sm text-gray-700">
              <div>
                <dt className="text-xs text-gray-500">Empresa</dt>
                <dd>{detail.request?.empresa}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Plan / importe</dt>
                <dd>
                  {detail.request?.planName} · {formatArs(detail.request?.expectedAmount)}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Estado licencia</dt>
                <dd>
                  {detail.license?.phase || '—'} · paidUntil {detail.license?.paidUntil || '—'}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Observación cliente</dt>
                <dd>{detail.request?.observation || '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Comprobante / ref.</dt>
                <dd>{detail.request?.receiptReference || detail.request?.paymentReference || '—'}</dd>
              </div>
              {detail.possibleMpConflict ? (
                <p className="text-amber-700 text-xs font-semibold">Posible conflicto con Mercado Pago</p>
              ) : null}
            </dl>
            <button
              type="button"
              className="mt-4 rounded bg-gray-200 px-3 py-2 text-sm"
              onClick={() => setDetail(null)}
            >
              Cerrar
            </button>
          </div>
        </div>
      ) : null}

      {approveOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          data-testid="manual-approve-modal"
        >
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
            <h3 className="text-lg font-bold text-gray-900">Aprobar y activar</h3>
            <p className="mt-2 text-sm text-gray-700">
              {approveOpen.empresa} · {approveOpen.planName} · {formatArs(approveOpen.expectedAmount)}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              El importe confirmado es el esperado (no editable). La activación usa el motor único.
            </p>
            <label className="mt-3 block text-xs font-semibold text-gray-600">
              Referencia validada *
              <input
                className="mt-1 w-full border rounded px-3 py-2 text-sm"
                value={approveRef}
                onChange={(e) => setApproveRef(e.target.value)}
                data-testid="manual-approve-reference"
              />
            </label>
            <label className="mt-3 block text-xs font-semibold text-gray-600">
              Monto confirmado (ARS)
              <input
                className="mt-1 w-full border rounded px-3 py-2 text-sm bg-gray-50"
                value={approveOpen.expectedAmount}
                readOnly
                data-testid="manual-approve-amount"
              />
            </label>
            <label className="mt-3 block text-xs font-semibold text-gray-600">
              Observación admin (opcional)
              <textarea
                className="mt-1 w-full border rounded px-3 py-2 text-sm"
                rows={2}
                value={approveObs}
                onChange={(e) => setApproveObs(e.target.value)}
              />
            </label>
            <label className="mt-3 flex items-start gap-2 text-sm text-gray-800">
              <input
                type="checkbox"
                checked={approveConfirm}
                onChange={(e) => setApproveConfirm(e.target.checked)}
                data-testid="manual-approve-confirm"
              />
              <span>Confirmo que verifiqué el pago y deseo activar este plan.</span>
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded border px-3 py-2 text-sm"
                disabled={approveBusy}
                onClick={() => setApproveOpen(null)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="rounded bg-emerald-600 text-white px-3 py-2 text-sm font-semibold disabled:opacity-50"
                disabled={approveBusy}
                onClick={confirmarAprobar}
                data-testid="manual-approve-submit"
              >
                {approveBusy ? 'Activando…' : 'Aprobar y activar'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {rejectOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          data-testid="manual-reject-modal"
        >
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
            <h3 className="text-lg font-bold text-gray-900">Rechazar solicitud</h3>
            <label className="mt-3 block text-xs font-semibold text-gray-600">
              Motivo *
              <select
                className="mt-1 w-full border rounded px-3 py-2 text-sm"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              >
                {REJECT_REASONS.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="mt-3 block text-xs font-semibold text-gray-600">
              Observación{rejectReason === 'otro' ? ' (obligatoria)' : ' (opcional)'}
              <textarea
                className="mt-1 w-full border rounded px-3 py-2 text-sm"
                rows={3}
                value={rejectObs}
                onChange={(e) => setRejectObs(e.target.value)}
              />
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className="rounded border px-3 py-2 text-sm" onClick={() => setRejectOpen(null)}>
                Volver
              </button>
              <button
                type="button"
                className="rounded bg-rose-600 text-white px-3 py-2 text-sm font-semibold disabled:opacity-50"
                disabled={rejectBusy}
                onClick={confirmarRechazar}
              >
                {rejectBusy ? 'Guardando…' : 'Rechazar'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {infoOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog">
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
            <h3 className="text-lg font-bold text-gray-900">Solicitar información</h3>
            <p className="mt-1 text-xs text-gray-500">La solicitud sigue en revisión; no se activa.</p>
            <textarea
              className="mt-3 w-full border rounded px-3 py-2 text-sm"
              rows={4}
              value={infoMsg}
              onChange={(e) => setInfoMsg(e.target.value)}
              placeholder="Qué necesitamos del cliente…"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className="rounded border px-3 py-2 text-sm" onClick={() => setInfoOpen(null)}>
                Volver
              </button>
              <button
                type="button"
                className="rounded bg-amber-600 text-white px-3 py-2 text-sm font-semibold disabled:opacity-50"
                disabled={infoBusy}
                onClick={confirmarInfo}
              >
                {infoBusy ? 'Enviando…' : 'Enviar pedido'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ManualPaymentAdminSection;
